import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { AccountTree, PlayArrow, Replay } from '@mui/icons-material';

import { IGwGPruefung } from '../../../Interfaces/IGwGPruefung';
import { useGwGPruefung } from '../../../hooks/reactQuerys/GwGPruefungQueries';
import { useAlert } from '../../../context/AlertContext';
import { GwGPflichtenListe } from './GwGPflichtenListe';
import { GwGPruefungDialog } from './GwGPruefungDialog';

interface IProps {
  idLegalTransaction: number;
}

/**
 * Abschnitt II des Vorgangsmanagers: Informationsaufbereitung.
 *
 * Die GwG-Prüfung nimmt, was im Vorgang schon steht — Vorgangsart,
 * Beteiligte, Anschriften —, führt den Fragenkatalog der BNotK darüber und
 * macht daraus eine Pflichtenliste. Die wird gegen den GwG-Ordner gehalten,
 * damit „haben wir alles?“ nicht gefragt, sondern beantwortet wird.
 */
export const GwGPruefungCard: React.FC<IProps> = ({ idLegalTransaction }) => {
  const { showAlert } = useAlert();
  const { starten, isPendingStarten } = useGwGPruefung();

  const [pruefung, setPruefung] = useState<IGwGPruefung | null>(null);
  const [offen, setOffen] = useState(false);

  async function oeffne() {
    try {
      const stand = await starten(idLegalTransaction);
      setPruefung(stand);
      setOffen(true);
    } catch (error) {
      showAlert({
        text: 'Die Prüfung konnte nicht geöffnet werden.',
        severity: 'error',
      });
    }
  }

  const laeuft = pruefung !== null && pruefung.Finished_at === null;
  const fertig = pruefung !== null && pruefung.Finished_at !== null;

  return (
    <Card variant='outlined' sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='flex-start'
          sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <AccountTree color='primary' />
            <Typography variant='h6'>II. Informationsaufbereitung</Typography>
          </Stack>

          <Button
            variant={laeuft ? 'outlined' : 'contained'}
            startIcon={laeuft || fertig ? <Replay /> : <PlayArrow />}
            disabled={isPendingStarten}
            onClick={oeffne}
          >
            {laeuft
              ? 'Prüfung fortsetzen'
              : fertig
                ? 'Prüfung erneut öffnen'
                : 'GwG-Prüfung starten'}
          </Button>
        </Stack>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Geldwäscherechtliche Prüfung nach dem Fragenkatalog der
          Bundesnotarkammer. Was im Vorgang bereits erfasst ist — Vorgangsart,
          Beteiligte, Anschriften —, wird vorgeschlagen und muss nur bestätigt
          werden. Am Ende steht eine Pflichtenliste, die gegen den GwG-Ordner
          abgeglichen ist.
        </Typography>

        {pruefung === null ? (
          <Alert severity='info' variant='outlined'>
            Für diesen Vorgang wurde noch keine Prüfung geöffnet. Die
            Verantwortung für die Erfüllung der geldwäscherechtlichen Pflichten
            bleibt in jedem Fall beim Notar; die Prüfung unterstützt dabei.
          </Alert>
        ) : (
          <Box>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}
            >
              <Chip
                size='small'
                color={fertig ? 'success' : 'warning'}
                label={fertig ? 'Abgeschlossen' : 'Läuft'}
              />
              {pruefung.Schritt && (
                <Chip
                  size='small'
                  variant='outlined'
                  label={pruefung.Schritt}
                />
              )}
              <Chip
                size='small'
                variant='outlined'
                label={`${pruefung.AntwortArray.length} Antworten`}
              />
              <Chip
                size='small'
                variant='outlined'
                label={`Katalogstand ${pruefung.CatalogVersion}`}
              />
            </Stack>

            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Haben wir alles?
            </Typography>
            <GwGPflichtenListe
              pflichten={pruefung.Pflichten}
              hatAntworten={pruefung.AntwortArray.length > 0}
            />
          </Box>
        )}
      </CardContent>

      {pruefung !== null && (
        <GwGPruefungDialog
          open={offen}
          pruefung={pruefung}
          setPruefung={setPruefung}
          onClose={() => setOffen(false)}
        />
      )}
    </Card>
  );
};
